import './index.scss';

import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

const EXIT_ANIMATION_MS = 200;

const ErrorList = ({ errors, onRemove, onClearAll, autoDismissMs }) => {
	const [leavingIds, setLeavingIds] = useState(new Set());
	const autoDismissTimeoutsRef = useRef(new Map());
	const removalTimeoutsRef = useRef(new Map());

	useEffect(() => {
		const activeAutoDismissTimeouts = autoDismissTimeoutsRef.current;
		const activeRemovalTimeouts = removalTimeoutsRef.current;
		return () => {
			activeAutoDismissTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
			activeAutoDismissTimeouts.clear();
			activeRemovalTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
			activeRemovalTimeouts.clear();
		};
	}, []);

	const handleRemove = useCallback((id) => {
		const autoDismissTimeoutId = autoDismissTimeoutsRef.current.get(id);
		if (autoDismissTimeoutId) {
			clearTimeout(autoDismissTimeoutId);
			autoDismissTimeoutsRef.current.delete(id);
		}

		let shouldScheduleRemoval = false;
		setLeavingIds((prev) => {
			if (prev.has(id)) return prev;
			const copy = new Set(prev);
			copy.add(id);
			shouldScheduleRemoval = true;
			return copy;
		});

		if (!shouldScheduleRemoval) return;

		const timeoutId = setTimeout(() => {
			onRemove(id);
			setLeavingIds((prev) => {
				const copy = new Set(prev);
				copy.delete(id);
				return copy;
			});
			removalTimeoutsRef.current.delete(id);
		}, EXIT_ANIMATION_MS);
		removalTimeoutsRef.current.set(id, timeoutId);
	}, [onRemove]);

	useEffect(() => {
		const errorIds = new Set(errors.map(({id}) => id));

		autoDismissTimeoutsRef.current.forEach((timeoutId, id) => {
			if (!errorIds.has(id) || autoDismissMs == null) {
				clearTimeout(timeoutId);
				autoDismissTimeoutsRef.current.delete(id);
			}
		});

		if (autoDismissMs == null) return;

		errors.forEach(({id}) => {
			if (leavingIds.has(id)) return;
			if (autoDismissTimeoutsRef.current.has(id)) return;

			const timeoutId = setTimeout(() => {
				handleRemove(id);
			}, autoDismissMs);
			autoDismissTimeoutsRef.current.set(id, timeoutId);
		});
	}, [autoDismissMs, errors, leavingIds, handleRemove]);

	const handleClearAll = () => {
		autoDismissTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
		autoDismissTimeoutsRef.current.clear();
		removalTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
		removalTimeoutsRef.current.clear();
		setLeavingIds(new Set());
		onClearAll();
	};

	if (errors.length === 0) return null;

	return (
		<div className="error-list">
			{errors.map(({ id, text }) => (
				<div key={id}
				     className={`error-banner${leavingIds.has(id) ? ' exit' : ''}`}>
					<span className="error-text">{text}</span>
					<button className="error-close" onClick={() => handleRemove(id)}>×</button>
				</div>
			))}

			{errors.length > 1 && (
				<button className="clear-all-errors" onClick={handleClearAll}>Clear All</button>
			)}
		</div>
	);
};

ErrorList.propTypes = {
	errors: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.any.isRequired,
			text: PropTypes.string.isRequired
		})
	).isRequired,
	onRemove: PropTypes.func.isRequired,
	onClearAll: PropTypes.func.isRequired,
	autoDismissMs: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
};

export default ErrorList;
